// PDF.js calls document.fonts.add(fontFace) in the parent window's JS context.
// The canvas lives in the popup's DOM, so ctx.font lookups use the popup's
// FontFaceSet — which never sees those fonts, producing garbled text.
//
// We patch FontFaceSet.prototype.add to synchronously mirror every new font
// into all open popup FontFaceSets before PDF.js awaits fontFace.loaded and
// starts drawing.
const _popupFontSets = new Set<FontFaceSet>();
let _origAdd: ((face: FontFace) => FontFaceSet) | null = null;
const _fontSources = new WeakMap<FontFace, BufferSource | string>();

async function _addToPopup(face: FontFace, target: FontFaceSet, OrigFontFace: typeof FontFace) {
  if (target.has(face)) return;

  const src = _fontSources.get(face);
  if (src) {
    // Always create a fresh FontFace for the popup using the original binary
    // source. A FontFace object from another window is silently accepted by
    // FontFaceSet.add() but the canvas renderer cannot use it for cross-window
    // rendering — we need a distinct object loaded in this window's context.
    // Capture _origAdd before the async gap in case the popup closes first.
    const origAdd = _origAdd;
    const copy = new OrigFontFace(face.family, src, {
      style: face.style,
      weight: face.weight,
      stretch: face.stretch,
      unicodeRange: face.unicodeRange,
      display: face.display,
    });
    try {
      await copy.load();
      if (!target.has(copy)) origAdd?.call(target, copy);
    } catch {
      // ignore — falls back to system fonts
    }
    return;
  }

  // No source tracked (font was created before the mirror was installed).
  // Try a direct cross-window add as a best-effort fallback.
  try {
    _origAdd!.call(target, face);
  } catch {
    // ignore
  }
}

export function installFontMirror() {
  if (_origAdd) return;

  const OrigFontFace = window.FontFace;

  (window as any).FontFace = function FontFace(
    family: string,
    source: BufferSource | string,
    descriptors?: FontFaceDescriptors,
  ) {
    const face = new OrigFontFace(family, source, descriptors);
    _fontSources.set(face, source);
    return face;
  };
  window.FontFace.prototype = OrigFontFace.prototype;

  // Patch FontFaceSet.prototype.add for synchronous mirroring.
  // Since FontFaceSet is not a named global in Chrome - reach it via prototype chain.
  const proto = Object.getPrototypeOf(document.fonts) as FontFaceSet;
  _origAdd = proto.add;

  proto.add = function (this: FontFaceSet, face: FontFace): FontFaceSet {
    _origAdd?.call(this, face);
    if (this === document.fonts) {
      _popupFontSets.forEach(target => _addToPopup(face, target, OrigFontFace));
    }
    return this;
  };
}

export function mirrorFontFaces(sourceDoc: Document, targetDoc: Document): () => void {
  installFontMirror();
  _popupFontSets.add(targetDoc.fonts);

  // Copy fonts already registered before this popup opened.
  const OrigFontFace = window.FontFace;
  sourceDoc.fonts.forEach(face => _addToPopup(face, targetDoc.fonts, OrigFontFace));

  return () => {
    _popupFontSets.delete(targetDoc.fonts);
  };
}
